import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Check, Gift, Loader2 } from "lucide-react";
import { MaskLines, Overline, Reveal, ease } from "./Primitives";
import { useLang, useT } from "@/lib/i18n";
import { works } from "@/lib/works";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Mock = ({ src, className, delay }) => (
  <motion.div
    className={`absolute w-[44%] aspect-[9/16] rounded-[26px] border-[5px] border-stone-800 bg-[#0c0a09] overflow-hidden shadow-2xl ${className}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1, delay, ease }}
  >
    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover object-top" />
  </motion.div>
);

const Visual = ({ tag }) => (
  <div className="relative h-[380px] sm:h-[520px]" data-testid="gift-visual">
    <Mock src={works[0].after} className="left-[4%] top-[8%] -rotate-6" delay={0} />
    <Mock src={works[3].after} className="right-[4%] top-0 rotate-6" delay={0.15} />
    <Mock src={works[1].after} className="left-[28%] top-[18%] z-10" delay={0.3} />
    <motion.div className="absolute z-20 left-[2%] -top-2 w-24 h-24 rounded-full bg-[var(--gold)] text-[#0c0a09] grid place-items-center font-display italic text-2xl"
      animate={{ rotate: [-8, 8, -8] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
      {tag}
    </motion.div>
  </div>
);

const Input = ({ id, label, ...rest }) => (
  <label className="block group">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400 group-focus-within:text-[var(--gold)] transition-colors">{label}</span>
    <input id={id} data-testid={`gift-${id}-input`} className="field" {...rest} />
  </label>
);

export const LeadMagnet = () => {
  const t = useT().gift;
  const privacyLabel = useT().cookies.privacy;
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", business: "" });
  const [kind, setKind] = useState("social");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(form.email)) { toast.error(t.invalid); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, kind, lang });
      setDone(true);
    } catch {
      toast.error(t.err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="gift" className="py-24 md:py-36 bg-[var(--bg2)] relative overflow-hidden">
      <div className="hero-glow" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <Overline>{t.over}</Overline>
          <h2 className="mt-8 font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight" data-testid="gift-title">
            <MaskLines lines={t.title} italicIndex={1} inView />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 text-stone-300 text-base md:text-lg max-w-lg">{t.sub}</p>
            <ul className="mt-6 space-y-2">
              {t.points.map((p) => (
                <li key={p} className="flex gap-3 text-stone-200"><Check size={18} className="text-[var(--gold)] mt-0.5 shrink-0" />{p}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.25}>
            {done ? (
              <div className="mt-10 rounded-[24px] border border-[var(--gold)]/50 bg-[#0c0a09]/70 p-8" data-testid="gift-success">
                <Gift className="text-[var(--gold)]" size={28} />
                <p className="mt-4 font-display text-3xl">{t.okTitle}</p>
                <p className="mt-2 text-stone-300">{t.ok}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 rounded-[24px] border border-[var(--line)] bg-[#0c0a09]/70 backdrop-blur-xl p-6 md:p-8 space-y-7" data-testid="gift-form">
                <div className="grid sm:grid-cols-2 gap-7">
                  <Input id="name" label={t.name} value={form.name} onChange={set("name")} maxLength={80} />
                  <Input id="email" label={t.email} type="email" value={form.email} onChange={set("email")} />
                </div>
                <Input id="business" label={t.business} value={form.business} onChange={set("business")} maxLength={120} />
                <div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{t.kind}</span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(t.kinds).map(([k, v]) => (
                      <button type="button" key={k} data-testid={`gift-kind-${k}`} onClick={() => setKind(k)}
                        className={`px-4 py-2 rounded-full text-sm border transition-colors duration-300 ${kind === k ? "bg-[var(--gold)] text-[#0c0a09] border-[var(--gold)]" : "border-[var(--line)] text-stone-300 hover:border-stone-400"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="gift-submit-btn" className="btn-gold w-full justify-center disabled:opacity-60">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> {t.sending}</> : <>{t.send} <ArrowUpRight size={18} /></>}
                </button>
                <p className="text-xs text-stone-400">{t.privacy} <Link to="/privacy" data-testid="gift-privacy-link" className="underline underline-offset-4 hover:text-[var(--gold)]">{privacyLabel}</Link></p>
              </form>
            )}
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Visual tag={t.tag} />
        </div>
      </div>
    </section>
  );
};
